import { request } from './request'

export interface Conversation {
  room_name: string;
  room_type?: 'direct' | 'group';
  participant_id?: string;
  participant_role?: 'client' | 'filmmaker';
  participant_name?: string;
  participant_avatar?: string;
  // Group chat fields
  group_name?: string;
  group_avatar?: string;
  participant_count?: number;
  participants?: Array<{
    entity_id: string;
    entity_role: 'client' | 'filmmaker';
    name: string;
    avatar?: string;
  }>;
  last_message: {
    content: string;
    created_at: string;
    user_id: string;
    has_attachments?: boolean;
    attachment_count?: number;
    message_type?: 'text' | 'media' | 'mixed' | 'voice';
    first_attachment?: {
      file_type?: string;
      file_name?: string;
      file_url?: string;
    };
  };
  unread_count: number;
  folder: 'primary' | 'requests';
  pinned_at?: string | null;
}

export interface ConversationState {
  room_name: string;
  entity_id: string;
  folder: 'primary' | 'requests';
  archived_at: string | null;
  muted_until: string | null;
  deleted_at?: string | null;
}

export interface ConversationWithState extends Conversation {
  state: ConversationState;
}

export const messagingApi = {
  listMessages: async (room: string, options?: { limit?: number; order?: 'asc' | 'desc'; offset?: number; before?: string }) => {
    let url = `/messages?room_name=${room}`;
    if (options?.limit) url += `&limit=${options.limit}`;
    if (options?.order) url += `&order=${options.order}`;
    if (options?.offset) url += `&offset=${options.offset}`;
    if (options?.before) url += `&before=${encodeURIComponent(options.before)}`;
    const data = await request(url);
    return data;
  },
  sendMessage: (payload: Record<string, unknown>) =>
    request(`/messages`, { method: 'POST', body: JSON.stringify(payload) }),
  getUserEmail: (entityId: string, entityRole: 'client' | 'filmmaker') =>
    request<{ email: string }>(`/messages/user-email?entity_id=${entityId}&entity_role=${entityRole}`),
  // New optimized endpoint that fetches all conversation data in one call
  getConversations: (entityId: string, entityRole: 'client' | 'filmmaker') =>
    request<Conversation[]>(`/conversations?entity_id=${entityId}&entity_role=${entityRole}`),

  // Get all conversations with state (for folder filtering and archived)
  getConversationsWithState: (
    entityId: string,
    entityRole: 'client' | 'filmmaker',
    options?: { folder?: 'primary' | 'requests'; includeArchived?: boolean }
  ) => {
    const params = new URLSearchParams({
      entity_id: entityId,
      entity_role: entityRole,
    });
    if (options?.folder) params.append('folder', options.folder);
    if (options?.includeArchived) params.append('include_archived', 'true');
    return request<ConversationWithState[]>(`/messages/conversations/list?${params.toString()}`);
  },

  // Get conversation state for a specific room
  getConversationState: (roomName: string, entityId: string) => {
    return request<ConversationState>(`/messages/conversations/state?room_name=${encodeURIComponent(roomName)}&entity_id=${entityId}`);
  },

  // Get conversation states for multiple rooms at once (batch - much faster)
  getConversationStatesBatch: (roomNames: string[], entityId: string) => {
    if (roomNames.length === 0) return Promise.resolve({} as Record<string, ConversationState>);
    return request<Record<string, ConversationState>>('/messages/conversations/states/batch', {
      method: 'POST',
      body: JSON.stringify({ room_names: roomNames, entity_id: entityId }),
    });
  },

  // Update conversation state (folder, archive, mute, delete)
  updateConversationState: (params: {
    roomName: string;
    entityId: string;
    entityRole: 'client' | 'filmmaker';
    folder?: 'primary' | 'requests';
    archived?: boolean;
    mutedUntil?: string | null;
    deleted?: boolean;
  }) => {
    return request<{ success: boolean; data: ConversationState }>('/messages/conversations/state', {
      method: 'PATCH',
      body: JSON.stringify({
        room_name: params.roomName,
        entity_id: params.entityId,
        entity_role: params.entityRole,
        folder: params.folder,
        archived: params.archived,
        muted_until: params.mutedUntil,
        deleted: params.deleted,
      }),
    });
  },

  // Archive a conversation
  archiveConversation: (roomName: string, entityId: string, entityRole: 'client' | 'filmmaker') => {
    return messagingApi.updateConversationState({
      roomName,
      entityId,
      entityRole,
      archived: true,
    });
  },

  // Unarchive a conversation
  unarchiveConversation: (roomName: string, entityId: string, entityRole: 'client' | 'filmmaker') => {
    return messagingApi.updateConversationState({
      roomName,
      entityId,
      entityRole,
      archived: false,
    });
  },

  // Permanently delete a conversation (won't appear anywhere including archives)
  deleteConversation: (roomName: string, entityId: string, entityRole: 'client' | 'filmmaker') => {
    return messagingApi.updateConversationState({
      roomName,
      entityId,
      entityRole,
      deleted: true,
    });
  },

  // Move conversation to requests folder
  moveToRequests: (roomName: string, entityId: string, entityRole: 'client' | 'filmmaker') => {
    return messagingApi.updateConversationState({
      roomName,
      entityId,
      entityRole,
      folder: 'requests',
    });
  },

  // Move conversation to primary folder
  moveToPrimary: (roomName: string, entityId: string, entityRole: 'client' | 'filmmaker') => {
    return messagingApi.updateConversationState({
      roomName,
      entityId,
      entityRole,
      folder: 'primary',
    });
  },

  // Pin a conversation
  pinConversation: (roomName: string, entityId: string, entityRole: 'client' | 'filmmaker') => {
    return request<{ success: boolean; pinned_at: string }>('/conversations', {
      method: 'PATCH',
      body: JSON.stringify({
        room_name: roomName,
        entity_id: entityId,
        entity_role: entityRole,
        action: 'pin',
      }),
    });
  },

  // Unpin a conversation
  unpinConversation: (roomName: string, entityId: string, entityRole: 'client' | 'filmmaker') => {
    return request<{ success: boolean; pinned_at: null }>('/conversations', {
      method: 'PATCH',
      body: JSON.stringify({
        room_name: roomName,
        entity_id: entityId,
        entity_role: entityRole,
        action: 'unpin',
      }),
    });
  },

  // ===== GROUP CHAT =====
  // Create a new group chat
  createGroupChat: (params: {
    name: string;
    description?: string;
    avatar_url?: string;
    participants: Array<{ entity_id: string; entity_role: 'client' | 'filmmaker' }>;
    creator_entity_id: string;
    creator_entity_role: 'client' | 'filmmaker';
  }) => {
    return request<{
      success: boolean;
      data: {
        room_name: string;
        name: string;
        room_type: 'group';
        description?: string;
        avatar_url?: string;
        participants: Array<{
          entity_id: string;
          entity_role: 'client' | 'filmmaker';
          role: 'admin' | 'member';
          joined_at: string;
        }>;
        pending_invites: Array<{
          id: string;
          invitee_entity_id: string;
          invitee_entity_role: 'client' | 'filmmaker';
        }>;
      };
    }>('/chat-rooms', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  // Get room details (for group chats)
  getRoomDetails: (roomName: string) => {
    return request<{
      room_name: string;
      name: string;
      room_type: 'direct' | 'group';
      description?: string;
      avatar_url?: string;
      participants: Array<{
        entity_id: string;
        entity_role: 'client' | 'filmmaker';
        role: 'admin' | 'member';
        name: string;
        avatar?: string;
        is_active: boolean;
      }>;
    }>(`/chat-rooms/${encodeURIComponent(roomName)}`);
  },

  // Leave group chat
  leaveGroupChat: (roomName: string, entityId: string, entityRole: 'client' | 'filmmaker') => {
    return request<{ success: boolean }>(`/chat-rooms/${encodeURIComponent(roomName)}/leave`, {
      method: 'POST',
      body: JSON.stringify({ entity_id: entityId, entity_role: entityRole }),
    });
  },

  // Get all filmmakers (for group member selection)
  getAllFilmmakers: () => {
    return request<Array<{
      filmmaker_id: string;
      first_name?: string;
      last_name?: string;
      profile_pic?: string;
      verified?: boolean;
      setup?: boolean;
    }>>('/filmmakers');
  },

  // Get all clients (for group member selection)
  getAllClients: () => {
    return request<Array<{
      client_id: string;
      company_name?: string;
      first_name?: string;
      last_name?: string;
      logo_url?: string;
      profile_pic?: string;
      verified?: boolean;
      setup?: boolean;
    }>>('/clients');
  },

  // Get pending group invites for current user
  getPendingInvites: (entityId: string, entityRole: 'client' | 'filmmaker') => {
    return request<{
      success: boolean;
      data: Array<{
        id: string;
        room_name: string;
        group_name: string;
        group_avatar?: string;
        group_description?: string;
        inviter_name: string;
        inviter_avatar?: string;
        member_count: number;
        message?: string;
        created_at: string;
        expires_at: string;
      }>;
    }>(`/chat-rooms/invites?entity_id=${entityId}&entity_role=${entityRole}`);
  },

  // Accept a group invite
  acceptInvite: (inviteId: string, entityId: string, entityRole: 'client' | 'filmmaker') => {
    return request<{
      success: boolean;
      data: {
        room: any;
        participant: any;
      };
    }>(`/chat-rooms/invites/${inviteId}/accept`, {
      method: 'POST',
      body: JSON.stringify({ entity_id: entityId, entity_role: entityRole }),
    });
  },

  // Decline a group invite
  declineInvite: (inviteId: string, entityId: string, entityRole: 'client' | 'filmmaker') => {
    return request<{ success: boolean; message: string }>(`/chat-rooms/invites/${inviteId}/decline`, {
      method: 'POST',
      body: JSON.stringify({ entity_id: entityId, entity_role: entityRole }),
    });
  },

  // Get user's group chats (for showing empty groups)
  getMyGroups: (entityId: string, entityRole: 'client' | 'filmmaker') => {
    return request<Array<{
      room_name: string;
      name: string;
      avatar_url?: string;
      description?: string;
      role: 'admin' | 'member';
      member_count: number;
      last_message?: {
        content: string;
        created_at: string;
      };
    }>>(`/chat-rooms?entity_id=${entityId}&entity_role=${entityRole}`);
  },

  // Send invite to add member to existing group (admin only)
  sendGroupInvite: (params: {
    roomName: string;
    entityId: string;
    entityRole: 'client' | 'filmmaker';
    invitedByEntityId: string;
    invitedByEntityRole: 'client' | 'filmmaker';
    message?: string;
  }) => {
    return request<{ success: boolean; data: any }>(`/chat-rooms/${encodeURIComponent(params.roomName)}/invite`, {
      method: 'POST',
      body: JSON.stringify({
        entity_id: params.entityId,
        entity_role: params.entityRole,
        invited_by_entity_id: params.invitedByEntityId,
        invited_by_entity_role: params.invitedByEntityRole,
        message: params.message,
      }),
    });
  },

  // ===== MESSAGE SEARCH =====
  // Search messages across all conversations or within a specific chat
  searchMessages: (params: {
    query: string;
    entityId: string;
    entityRole: 'client' | 'filmmaker';
    roomName?: string; // Optional - for in-chat search
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams({
      q: params.query,
      entity_id: params.entityId,
      entity_role: params.entityRole,
    });
    if (params.roomName) searchParams.append('room_name', params.roomName);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());

    return request<{
      results: Array<{
        message_id: string;
        content: string;
        room_name: string;
        created_at: string;
        sender_id: string;
        sender_role: 'client' | 'filmmaker';
        sender_name?: string;
        sender_avatar?: string;
        conversation_name?: string;
        conversation_avatar?: string;
      }>;
      total_count: number;
    }>(`/message-search?${searchParams.toString()}`);
  },
} 